import * as React from "react";

import { cn } from "@/lib/utils";

import { MuiLink } from "./mui-link";

// `@modsy/react-ui`'s `Breadcrumbs` (`packages/react-ui/src/breadcrumbs/`), a
// wrapper over MUI's own, which the donor theme never overrides — so
// `Breadcrumbs.js` is the spec, as the details page draws it
// (`SpecDetailsBreadcrumbs.tsx`) and measured on the running donor (#694):
//
//   - the root: a `nav` named "breadcrumb", its text body1 in `text.secondary`
//     (`rgba(0,0,0,.6)`, 16px / 24px);
//   - the list: an `ol`, flex, wrapping, centred, no padding, margin or marks;
//   - between two crumbs, a `/` in its own `li`, `aria-hidden`, 8px each side
//     (`Breadcrumbs.js`'s `BreadcrumbsSeparator`), not selectable;
//   - a **link** crumb (`BreadcrumbsLink`): MUI's `Link` in `primary.main`,
//     underlined on hover only, no wrapping;
//   - an **item** crumb (`BreadcrumbsItem`): a span in the inherited ink, no
//     wrapping.
//
// MUI's `maxItems` collapse (8) is not ported: nothing here draws more than two.

export function Breadcrumbs({
  children,
  className,
  ...props
}: Omit<React.ComponentPropsWithoutRef<"nav">, "aria-label">) {
  const crumbs = React.Children.toArray(children);
  return (
    <nav
      {...props}
      aria-label="breadcrumb"
      data-slot="breadcrumbs"
      className={cn("text-lennar-ink-secondary text-base leading-normal", className)}
    >
      <ol className="m-0 flex list-none flex-wrap items-center p-0">
        {crumbs.map((crumb, index) => (
          <React.Fragment key={index}>
            {index > 0 ? (
              <li aria-hidden="true" className="mx-2 flex select-none">
                /
              </li>
            ) : null}
            <li>{crumb}</li>
          </React.Fragment>
        ))}
      </ol>
    </nav>
  );
}

/**
 * A crumb that goes somewhere. Hand it the router's `Link` as its one child;
 * it dresses it (`MuiLink asChild`).
 */
export function BreadcrumbLink({ children }: { children: React.ReactElement }) {
  return (
    <MuiLink asChild underline="hover" className="flex items-center gap-1 whitespace-nowrap">
      {children}
    </MuiLink>
  );
}

/** The current crumb: plain text in the list's ink. */
export function BreadcrumbItem({ children }: { children: React.ReactNode }) {
  return <span className="flex items-center gap-1 whitespace-nowrap">{children}</span>;
}
